import { getLevel, sampleRound, type LevelNumber } from './progress'
import { roundShape, type GameProgress } from './mastery'
import type { Band, GameContent, Item, Level } from './types'

/**
 * The round assembler — the piece `mastery.ts` names and did not have.
 *
 * ─── §1 · the gap this closes ───────────────────────────────────────────────
 *
 * `roundShape()` has computed a supported round since PROGRESSION §3 was
 * written, and nothing called it. Its own header says so:
 *
 *     ⚠️ NOTHING CALLS THIS YET — CONFORMANCE C10, found in HH-4. […] Every
 *     round in the product is `sampleRound(levelData, pick)`, which is handed
 *     ONE level and returns whole items. It cannot reach the level below for a
 *     review scene […] The gap is invisible from here because this function is
 *     correct and its unit tests pass. Do not "fix" it by changing anything in
 *     this file: the missing piece is a round assembler that consults it.
 *
 * This is that assembler. It is the ONLY thing here that reaches across two
 * levels, which is why it is a new module rather than a change to `sampleRound`
 * — that function's contract is *one level in, whole items out*, four other
 * callers depend on it, and widening it would have made every game adaptive by
 * accident rather than on purpose.
 *
 * ─── §2 · what "easier" means here, and what it does not ────────────────────
 *
 * 🔴 EASIER MEANS QUESTIONS FROM THE LEVEL BELOW. A child at L2 who is missing
 * gets some of L1's counting back inside an otherwise normal L2 round — the
 * thing a teacher would do, and what `RoundShape.reviewScenes` has always
 * meant: *"how many of this round's scenes come from the previous level's
 * pool"*.
 *
 * ⚠️ THIS IS NOT A DEMOTION, AND THE DIFFERENCE MATTERS. The child's `level` is
 * untouched; `endRound` still owns that. They are not moved down, told they are
 * behind, or shown a different screen. One round in three is a question they can
 * answer, inside the level they are on.
 *
 * 🔴 THE ROUND IS THE SAME LENGTH, ALWAYS. `itemCount` items go out no matter
 * how much review is mixed in, because every round machine walks an integer
 * index to `items.length` and a short round reads to a child as the game ending
 * early — the same property `sampleRound`'s band fallback protects.
 *
 * ⚠️ `shape.choiceCount` IS DELIBERATELY NOT APPLIED HERE. Dropping a card is a
 * per-game decision, not a curriculum one: Sky Quest answers by STANDING on one
 * of three floor zones, so a two-card round on a three-zone floor would leave a
 * child standing somewhere no answer exists. A game that wants fewer cards must
 * reduce its zones in the same breath, which only that game can do. This module
 * returns items; it does not reshape them.
 *
 * ─── §3 · what it must never do ─────────────────────────────────────────────
 *
 * 🔴 IT NEVER INVENTS CONTENT. Review comes out of the previous level's own
 * table through the same `sampleRound`, so it is de-duplicated by spoken line
 * and its choices are shuffled exactly as any other question is. If the level
 * below cannot supply enough, the round is topped back up from the current
 * level rather than run short — the honest failure, and the same one
 * `adaptive.ts` §2 chooses.
 *
 * 🔴 AND IT IS INVISIBLE. Nothing here returns a flag a screen could render at
 * a child. `mastery.ts` §3: *"The child is never told the difficulty changed
 * […] a five-year-old who works that out has been told they are behind."* The
 * REPORT is where support is disclosed, to an adult, which is what
 * `supportSummary` below is for.
 */

/* ──────────────────────────────── §4 · responding to a bad run ── */

/**
 * 🔴 `roundShape` ALONE DOES NOT ANSWER "THIS CHILD IS MISSING", AND THAT IS
 * THE GAP THIS SECTION FILLS.
 *
 * Read it: `reviewScenes` is `level === 1 ? 0 : scaffolded ? 2 : 1`. It depends
 * on the SCAFFOLD — which only ever turns on after a *ceiling* advance — and on
 * nothing else. Its `easing` branch, the one that does respond to time at a
 * level, moves `choiceCount` instead. So a child at L2 who has missed five of
 * their last six questions gets exactly the same one review scene as a child
 * who has got them all right, and the only thing that changes for them is a
 * card count this game cannot drop (§2).
 *
 * ⚠️ SO THE MISS RESPONSE IS NEW POLICY, NOT A RULE READ OFF A SPEC. It is
 * written here rather than in `mastery.ts` because that file says in terms not
 * to fix the gap by changing it, and because *how many questions this round
 * takes from below* is the assembler's decision — `roundShape` supplies the
 * floor, this raises it when the record says the level is too hard.
 *
 * ⚠️ IT READS `lastSixFirstTry`, THE SAME ARRAY MASTERY GATES ON, so it
 * inherits mastery §2 for free: no gesture, no speed, no retry can lower a
 * child's difficulty, because none of them is in that array. First-try
 * pointing, and nothing else.
 */

/**
 * Misses in the last six that mean the level is running ahead of the child.
 *
 * Three of six is 50%, against the ~85% success band `adaptive.ts` steers to
 * and the 5-of-6 mastery needs. Two would fire on one bad pair of questions,
 * which every child has.
 */
export const MISSES_FOR_REVIEW = 3

/**
 * Misses that mean the level is not landing at all, and the round should be
 * mostly things the child can already do.
 *
 * 🔴 FIVE OF SIX, NOT SIX OF SIX. Waiting for a clean sweep of failure would
 * withhold help from the child who got one lucky guess, which is the child this
 * is for.
 */
export const MISSES_FOR_HEAVY_REVIEW = 5

/**
 * Extra review scenes this child's recent record has earned, on top of
 * whatever `roundShape` already grants.
 *
 * ⚠️ AN EMPTY OR PART-FULL WINDOW EARNS NOTHING. A child three questions into
 * their first round at a level has not yet shown anything, and easing on two
 * misses out of two would make every level open at the level below it.
 */
export function extraReviewFor(progress: GameProgress): number {
  const window = progress.lastSixFirstTry
  if (window.length < 6) return 0
  const misses = window.filter((v) => v === 0).length
  if (misses >= MISSES_FOR_HEAVY_REVIEW) return 2
  if (misses >= MISSES_FOR_REVIEW) return 1
  return 0
}

/** What a round turned out to be, for the report. Never shown to the child. */
export interface AssembledRound {
  /** The round, in the order it should be played. */
  items: Item[]
  /** How many of `items` came from the level below. Zero on a normal round. */
  reviewScenes: number
  /** The level the review came from, or null when there was none. */
  reviewFrom: LevelNumber | null
  /** True while the child is being supported after a ceiling advance. */
  scaffolded: boolean
}

/**
 * The spoken line, as `sampleRound` keys it.
 *
 * 🔴 THE SAME KEY, DELIBERATELY. `sampleRound` de-duplicates within one level by
 * the sentence a child hears; two levels can still carry the same sentence, and
 * a round that asked *"How many stars?"* once from L1 and once from L2 would be
 * the exact repeat that rule exists to stop — just across the seam this module
 * introduces.
 */
const spokenKey = (item: Item): string => item.prompt.en.trim().toLowerCase()

/**
 * A deterministic shuffle, so review is spread through the round rather than
 * bunched at one end.
 *
 * 🔴 IT MATTERS BECAUSE THE INITIAL ORDER IS WHAT THE CHILD MEETS.
 * `orderForAim` only reorders once an outcome has been reported, so whatever
 * this returns is the first question asked. Review appended at the end would put
 * the whole of a struggling child's support after the questions they were
 * already missing.
 *
 * ⚠️ `pick`, NOT `Math.random` — the same reason `shuffleChoices` takes it: a
 * round has to stay reproducible in a test and in a replay.
 */
function shuffled(items: Item[], pick: (n: number) => number): Item[] {
  const out = [...items]
  for (let i = out.length - 1; i > 0; i--) {
    const j = pick(i + 1)
    const tmp = out[i]
    out[i] = out[j]
    out[j] = tmp
  }
  return out
}

/**
 * Builds one round for this child, at the level they are on, with whatever
 * support `roundShape` says they have earned.
 *
 * Replaces `sampleRound(getLevel(content, level), pick)` at the call site. A
 * game with no `GameProgress` to hand should keep calling `sampleRound` — this
 * needs the progress to know there is anything to do.
 */
export function assembleRound(
  content: GameContent,
  progress: GameProgress,
  pick: (n: number) => number,
  band?: Band,
  /** Item ids from the last round — see `sampleRound`. */
  avoid?: ReadonlySet<string>,
): AssembledRound {
  const shape = roundShape(progress)
  const level: Level = getLevel(content, progress.level)
  const size = Math.max(1, level.itemCount)

  /*
   * 🔴 LEVEL 1 HAS NOTHING BELOW IT, AND `roundShape` ALREADY SAYS SO — it
   * returns `reviewScenes: 0` at level 1. This re-checks rather than trusting
   * it, because `getLevel(content, 0)` would index off the front of the tuple
   * and hand back `undefined` as a `Level`, which type-checks here and then
   * throws on `.items` somewhere far from this line.
   */
  const wanted =
    progress.level > 1 ? shape.reviewScenes + extraReviewFor(progress) : 0

  /*
   * ⚠️ REVIEW MAY TAKE AT MOST HALF THE ROUND, AND THE CAP IS THE POINT.
   *
   * A round with no current-level question in it is not a supported L2 round,
   * it is an L1 round wearing L2's label — and it would stall the child where
   * they are while `roundsAtLevel` kept counting toward a ceiling advance they
   * had not practised for. Half is the most support that still leaves the level
   * being taught: a child missing every question gets three questions they can
   * answer and three of the thing they are here to learn.
   *
   * 🔴 SO THE FLOOR IS ON THE CURRENT LEVEL, NOT ON REVIEW. Whatever the
   * record says, at least half of every round is the level the child is on.
   */
  const reviewWanted = Math.min(wanted, Math.floor(size / 2))

  const main = sampleRound(level, pick, band, avoid)
  if (reviewWanted <= 0) {
    return {
      items: main,
      reviewScenes: 0,
      reviewFrom: null,
      scaffolded: shape.scaffolded,
    }
  }

  const below = (progress.level - 1) as LevelNumber
  /* The whole level below, in the sampler's order — a round-sized sample often held no unused answer. */
  const lower = getLevel(content, below)
  const reviewPool = sampleRound({ ...lower, itemCount: lower.items.length }, pick, band, avoid)

  /*
   * The current level keeps at least half the round; review takes the rest.
   * `main` is already de-duplicated and shuffled by the sampler, so trimming
   * from the end drops whole questions rather than biasing toward any strand.
   */
  const keep = main.slice(0, size - reviewWanted)
  const heard = new Set(keep.map(spokenKey))
  const answered = new Set(keep.map((it) => String(it.target)))

  /*
   * ⚠️ AN UNUSED ANSWER FIRST, THEN ANY UNHEARD LINE — the sampler's own rule,
   * across the join. The two halves are sampled apart, so without this a review
   * scene could ask for the number the main half just did.
   */
  const review: Item[] = []
  for (const sameAnswerOk of [false, true]) {
    for (const it of reviewPool) {
      if (review.length >= reviewWanted) break
      if (heard.has(spokenKey(it))) continue
      if (!sameAnswerOk && answered.has(String(it.target))) continue
      heard.add(spokenKey(it))
      answered.add(String(it.target))
      review.push(it)
    }
  }

  /*
   * 🔴 TOPPED BACK UP FROM THE CURRENT LEVEL, NEVER RUN SHORT. If the level
   * below could not supply enough unheard questions — a small table, or one that
   * shares its sentences with this level — the round is filled from the items
   * `keep` left behind rather than handed back a scene light.
   */
  const filled = [...keep, ...review]
  for (const it of main.slice(size - reviewWanted)) {
    if (filled.length >= size) break
    if (heard.has(spokenKey(it))) continue
    heard.add(spokenKey(it))
    filled.push(it)
  }

  return {
    items: shuffled(filled, pick),
    reviewScenes: review.length,
    reviewFrom: review.length > 0 ? below : null,
    scaffolded: shape.scaffolded,
  }
}

/**
 * How much support a child is getting, in words an adult reads — the report
 * side of §2's "invisible to the child, disclosed to the grown-up".
 *
 * 🔴 IT DESCRIBES THE ROUND, NOT THE CHILD. "2 questions from Level 1" is a
 * fact about what was asked. "Struggling with Level 2" is a judgement about a
 * four-year-old, and it is not this function's to make — a teacher reading the
 * report makes it, with the accuracy figures beside this.
 */
export function supportSummary(round: AssembledRound): string | null {
  if (round.reviewScenes <= 0) return null
  const scenes = round.reviewScenes === 1 ? '1 question' : `${String(round.reviewScenes)} questions`
  return `${scenes} from Level ${String(round.reviewFrom ?? 1)}`
}
